<template>
  <Tooltip v-if="disabled" position="left">
    <template v-slot:text>
      <Button severity="primary" :disabled="disabled">
        <IconPlus />
        New Zone
      </Button>
    </template>
    <template v-slot:tooltip> Select sensors to add </template>
  </Tooltip>
  <template v-else>
    <Button severity="primary" @click="visible = true" :disabled="disabled">
      <IconPlus />
      New Zone
    </Button>
  </template>

  <DialogFrame
    v-model:visible="visible"
    :loading="loading"
    @onHide="onHide"
    @onClose="closeModal"
    class="p-dialog-breathe"
  >
    <template #title>
      Create Zone
      <Tooltip position="left">
        <template v-slot:text>
          <IconInfo />
        </template>
        <template v-slot:tooltip>
          A zone consists of at least one sensor and represents a place of interest (PoI) for
          visitor insights at your event. Create a new zone for each PoI at your event.
        </template>
      </Tooltip>
    </template>

    <template #body>
      <div class="modal-body-add-zone">
        <DialogZoneForm
          v-if="stage === 1"
          v-model:newEvent="newEvent"
          v-model:zone="newZone"
          :index="-1"
          @changeStage="goToStageTwo"
        />

        <DialogZoneSensors
          v-if="stage === 2"
          v-model:newEvent="newEvent"
          v-model:zone="newZone"
          :index="-1"
          @changeStage="goToStageOne"
        />
      </div>
    </template>

    <template #footer>
      <Button
        v-if="stage === 1"
        :disabled="loading || !zoneValid"
        label="Save Changes"
        severity="secondary"
        @click="saveModal()"
        autofocus
      />
      <Button
        v-if="stage === 2"
        :disabled="loading"
        label="Done"
        severity="primary"
        @click="stage = 1"
        autofocus
      />
    </template>
  </DialogFrame>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

import type { SensorType } from '@/types/Sensor';
import type { EventTypeNew, ZoneType } from '@/types/Event';

import { Button } from 'primevue';

import DialogFrame from './DialogFrame.vue';
import DialogZoneForm from './forms/DialogZoneForm.vue';
import DialogZoneSensors from './forms/DialogZoneSensors.vue';

import Tooltip from '@/components/global/Tooltip.vue';

import IconInfo from '@/icons/IconInfo.svg?component';
import IconPlus from '@/icons/IconPlus.svg?component';

const newEvent = defineModel<EventTypeNew>('newEvent');

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    defaultSensors?: SensorType[];
  }>(),
  {
    disabled: false,
  },
);

const newZone = ref<ZoneType>({
  name: '',
  categories: [],
  clusters: [],
  sensors: [],
});

const emit = defineEmits(['onZoneAdded', 'onZoneCanceled']);

const visible = ref(false);
const loading = ref(false);
const stage = ref(1);

watch(visible, (newVal) => {
  if (newVal === true) {
    if (props.defaultSensors) {
      newZone.value.sensors = props.defaultSensors;
    }
  }
});

const saveModal = () => {
  emit('onZoneAdded', newZone.value);
  onHide();
};

const closeModal = () => {
  onHide();
};

const onHide = () => {
  stage.value = 1;
  visible.value = false;
  newZone.value = {
    name: '',
    categories: [],
    clusters: [],
    sensors: [],
  };
};

const hasUniqueName = computed(() => {
  let nameUnique = true;

  newEvent.value?.zones.forEach((zone) => {
    if (zone.name.toLowerCase().trim() === newZone.value.name.toLowerCase().trim()) {
      nameUnique = false;
    }
  });

  return nameUnique;
});

const zoneValid = computed(() => {
  const hasName = newZone.value.name.trim() !== '';
  const hasType = newZone.value.categories.length > 0;
  const hasSensor = newZone.value.sensors.length > 0;
  let sensorsValid = true;

  newZone.value.sensors.forEach((sensor) => {
    if (!sensor.engaged_signal || !sensor.passerby_signal) {
      sensorsValid = false;
    }
  });

  return hasName && hasType && hasSensor && hasUniqueName.value && sensorsValid;
});

const goToStageTwo = () => {
  stage.value = 2;
};

const goToStageOne = () => {
  stage.value = 1;
};
</script>
